import { midiToFreq } from '../../utils/math';
import { getScaleNotes } from '../../utils/music/scales';

import { MelodyGenerator } from './MelodyGenerator';
import { HarmonyEngine } from './HarmonyEngine';
import { StructureEngine } from './StructureEngine';
import { RhythmEngine } from './RhythmEngine';
import { ComposerSession } from './ComposerSession';
import { VoiceStrategy } from './voices/VoiceStrategy';
import { MaestroVoice } from './voices/MaestroVoice';
import { SoloistVoice } from './voices/SoloistVoice';

// V11: The Bandmate Update - New Intelligence Modules
import { ArrangementIntelligence } from './ArrangementIntelligence';
import { SessionJourney } from './SessionJourney';
import { GhostMusician, GhostContext } from './GhostMusician';
import { EmotionIntelligence } from './EmotionIntelligence';
import { SpecialFX, FXContext } from './SpecialFX';

/**
 * ComposerOrchestrator
 * The "Brain" that processes touches and directs the sub-engines.
 * It is STATELESS regarding the Session (it accepts a Session object),
 * but STATEFUL regarding the Sub-Engines (MelodyGenerator, etc).
 * 
 * V11 ENHANCEMENTS:
 * - ArrangementIntelligence: Auto-thinning + comeback moments
 * - SessionJourney: Emotional arc + peak callbacks
 * - GhostMusician: Intelligent countermelody + dynamic presence
 */
export class ComposerOrchestrator {
    private melodyGenerator: MelodyGenerator;
    private harmonyEngine: HarmonyEngine;
    private structureEngine: StructureEngine;
    private rhythmEngine: RhythmEngine;

    // V11: New Intelligence Modules
    private arrangementIntelligence: ArrangementIntelligence;
    private sessionJourney: SessionJourney;
    private ghostMusician: GhostMusician;
    private emotionIntelligence: EmotionIntelligence;
    private specialFX: SpecialFX;

    private voices: VoiceStrategy[];

    // V11: Track last touch for Ghost Musician context
    private lastTouchTime: number = 0;
    private lastUserYPosition: number | null = null;

    constructor() {
        this.melodyGenerator = new MelodyGenerator();
        this.harmonyEngine = new HarmonyEngine();
        this.structureEngine = new StructureEngine();
        this.rhythmEngine = new RhythmEngine();

        // V11: Initialize new modules
        this.arrangementIntelligence = new ArrangementIntelligence();
        this.sessionJourney = new SessionJourney();
        this.ghostMusician = new GhostMusician();
        this.emotionIntelligence = new EmotionIntelligence();
        this.specialFX = new SpecialFX();

        const maestro = new MaestroVoice(
            this.harmonyEngine,
            this.rhythmEngine,
            this.melodyGenerator,
            this.structureEngine
        );

        const soloist = new SoloistVoice(
            this.rhythmEngine,
            this.melodyGenerator,
            this.structureEngine
        );

        // Configure Voices: Index 0 = Maestro, Index 1+ = Soloist
        this.voices = [maestro, soloist];
    }

    /**
     * Resets all sub-engines. Call this when Session is re-initialized.
     */
    reset(session: ComposerSession): void {
        this.melodyGenerator.reset();
        this.melodyGenerator.randomizeParams(session.currentMood);
        this.melodyGenerator.setMotifStrategy(session.motifStrategy);
        this.harmonyEngine.reset();
        this.structureEngine.reset(session.structureDefinition.sections);
        this.rhythmEngine.reset();

        // AUDIT FIX: Set song-level DNA for persistent rhythmic identity
        this.rhythmEngine.setSongDNA(session.songDNA);

        // V11: Reset new modules
        this.arrangementIntelligence.reset();
        this.sessionJourney.reset();
        this.ghostMusician.reset();
        this.emotionIntelligence.reset();
        this.specialFX.reset();
        this.lastTouchTime = Date.now();
        this.lastUserYPosition = null;
    }

    /**
     * Accessor for StructureEngine (needed for constraints calculation in main engine if strictly necessary, 
     * but better to handle internally).
     */
    getCurrentSection() {
        return this.structureEngine.getCurrentSection();
    }

    // For orchestration data needed by external visualizers or logs
    getSectionDynamics() {
        return this.structureEngine.getSectionDynamics();
    }

    /**
     * The Main Logic Loop
     */
    getNotesForTouch(
        session: ComposerSession,
        voiceIndex: number,
        tuning: number = 440,
        dynamics?: { velocity: number; yPos: number }
    ): Array<{
        freq: number,
        delay: number,
        duration?: number,
        velocity?: number,
        timbre?: { filter: number, release: number, reverb: number },
        dampAll?: boolean
    }> {
        session.touchCount++;
        this.lastTouchTime = Date.now();
        if (dynamics) {
            this.lastUserYPosition = dynamics.yPos;
        }

        // AUDIT FIX 1.4: Record touch for GrooveLock ONCE per touch event
        this.rhythmEngine.recordTouchForGroove();

        // =====================================================
        // V11: EMOTION INTELLIGENCE (Anticipation / Release)
        // Get emotion hint first to influence arrangement
        // =====================================================
        const emotionHint = this.emotionIntelligence.getHint(session.touchCount);

        // =====================================================
        // V11: ARRANGEMENT INTELLIGENCE
        // Get arrangement hint BEFORE generating notes
        // =====================================================
        const arrangementHint = this.arrangementIntelligence.getHint();

        // Auto-thinning: Skip this note if arrangement says to drop
        if (arrangementHint.shouldDropNote && voiceIndex > 0) {
            // Only drop secondary voices, never the primary (Maestro)
            return [];
        }

        // 1. Get Context
        const currentSection = this.structureEngine.getCurrentSection();
        const constraints = session.getConstraints(currentSection);
        const scaleNotes = getScaleNotes(constraints);

        if (scaleNotes.length === 0) {
            return [{ freq: midiToFreq(60 + (session.octaveOffset * 12), tuning), delay: 0 }];
        }

        // =====================================================
        // V11: SESSION JOURNEY
        // Record this touch and check for peak callbacks
        // =====================================================
        const intensity = dynamics?.velocity ?? 0.7;
        this.sessionJourney.recordTouch(intensity, session.currentChordContext, session.touchCount);
        const journeyHint = this.sessionJourney.getHint(session.touchCount);

        // AUDIT FIX 1.2: Wire SessionJourney into harmony
        if (journeyHint.shouldRecallPeak && journeyHint.recalledChord) {
            session.journeyOverride = journeyHint.recalledChord;
        } else {
            session.journeyOverride = undefined;
        }

        // Record melody for Ghost Musician counterpoint
        if (session.lastMelodyNote !== null) {
            this.ghostMusician.recordUserMelody(session.lastMelodyNote);
        }

        // 2. Orchestration Settings
        const orchSettings = this.structureEngine.getOrchestrationSettings();

        // FEATURE 2: TIMBRE MORPHING ("Emotional Reflex")
        // Velocity (0-1) modulates Filter brilliance and Release time
        let filterCutoff = orchSettings.filterCutoff;
        let releaseTime = orchSettings.releaseTime;

        if (dynamics && dynamics.velocity !== undefined) {
            // EXCEPTION: Pianos should not have heavy low-pass filtering on low velocity.
            // SHARPEN EP: Allow electric-piano to be modulated, but keep piano strictly open.
            const isEP = session.suggestedInstrument === 'electric-piano';
            const shouldModulateFilter = session.suggestedInstrument !== 'piano' && (isEP || session.suggestedInstrument !== 'electric-piano');
            // Wait, logic check: 
            // if 'piano' -> false
            // if 'electric-piano' -> true (assuming we want to sharpen it)
            // if 'synth' -> true
            // Corrected:
            const canModulate = session.suggestedInstrument !== 'piano';

            if (canModulate) {
                // For EP, we want a higher floor so it stays sharp.
                const floor = isEP ? 0.6 : 0.3;
                const velMult = floor + (dynamics.velocity * (1.0 - floor));
                filterCutoff *= velMult;
            }
        }

        const timbreData = {
            filter: filterCutoff,
            release: releaseTime,
            reverb: orchSettings.reverbMix
        };
        const sectionVelMult = this.structureEngine.getSectionDynamics();
        let baseVelocity = dynamics ? dynamics.velocity : 0.7;

        // =====================================================
        // V11: COMEBACK MOMENT - Boost velocity after silence
        // =====================================================
        if (arrangementHint.isComebackMoment) {
            baseVelocity = Math.min(1.0, baseVelocity + arrangementHint.velocityBoost);
            if (process.env.NODE_ENV === 'development') {
                console.log(`[V11] Comeback moment! Velocity boosted by ${arrangementHint.velocityBoost.toFixed(2)}`);
            }
        }

        // Apply EMOTION VELOCITY BOOST
        if (emotionHint.velocityBoost > 0) {
            baseVelocity = Math.min(1.0, baseVelocity + emotionHint.velocityBoost);
        }

        // Apply EMOTION DENSITY FORCE
        // If emotion wants dense, override arrangement hint
        const effectiveDensity = emotionHint.forceDensity || arrangementHint.densityLevel;

        // 3. Delegation (Unified)
        // Map any index > 1 to the Soloist (Index 1)
        const effectiveVoiceIndex = Math.min(voiceIndex, this.voices.length - 1);
        const voiceStrategy = this.voices[effectiveVoiceIndex];

        const { events, stateUpdate } = voiceStrategy.getNextEvent(
            session,
            scaleNotes,
            baseVelocity,
            sectionVelMult,
            timbreData,
            tuning,
            dynamics,
            arrangementHint.densityLevel // AUDIT FIX 1.5: Unified density source
        );

        // =====================================================
        // V11: Musical Awesomeness (FX Layers)
        // 1. The Void (Silence Drop) - Overrides everything
        // 2. Glissando Trails (Virtuoso Runs)
        // 3. Harmonic Bloom (Sparse chords)
        // =====================================================

        // 1. THE VOID
        const shouldTriggerVoid = this.arrangementIntelligence.checkForVoid(dynamics?.velocity || 0);
        if (shouldTriggerVoid) {
            // KILL SOUND
            return [{
                freq: 0,
                delay: 0,
                duration: 0,
                velocity: 0,
                dampAll: true  // Special flag to kill reverb tails
            }];
            // Or return empty with side effect? 
            // ToneAudioService handles dampAll if present in event.
        }

        // 2. SPECIAL FX
        const fxContext: FXContext = {
            scaleNotes,
            lastTouchFreq: events[0]?.freq || 440,
            lastTouchVelocity: baseVelocity,
            density: arrangementHint.densityLevel,
            tuning
        };

        // Harmonic Bloom (Low Density)
        // Only if we haven't triggered anticipation or void
        // P4.1: Suppress bloom during finale/preparation to avoid breaking dramatic endings
        const conclusionState = this.structureEngine.getConclusionState();
        if (emotionHint.state === 'neutral' && conclusionState === 'neutral') {
            const bloomEvents = this.specialFX.getBloomEvents(fxContext);
            if (bloomEvents.length > 0) {
                events.push(...bloomEvents);
            }
        }

        // 4. EMOTION POST-PROCESSING (Register Shift)
        if (emotionHint.registerOffset !== 0) {
            const semitoneRatio = Math.pow(2, 1 / 12);
            events.forEach((event, idx) => {
                // Apply semitone shift to frequency
                const multiplier = Math.pow(semitoneRatio, emotionHint.registerOffset);
                if (!isNaN(multiplier)) {
                    event.freq *= multiplier;
                }
            });
        }

        // 4. State Application (Explicit)
        if (stateUpdate) {
            if (stateUpdate.currentChordContext) {
                session.currentChordContext = stateUpdate.currentChordContext;
            }
            if (stateUpdate.currentBassNote !== undefined) {
                session.currentBassNote = stateUpdate.currentBassNote;
            }
            if (stateUpdate.lastMelodyNote !== undefined) {
                session.lastMelodyNote = stateUpdate.lastMelodyNote;
            }
        }

        // =====================================================
        // V11: OCTAVE DOUBLING on comeback/dense moments
        // P4.2: Suppress doubling during finale to preserve dramatic pause
        if (arrangementHint.layerPresence.octaveDoubling && events.length > 0 && conclusionState === 'neutral') {
            // Double the bass note an octave down
            const bassEvent = events.find(e => e.freq === Math.min(...events.map(ev => ev.freq)));
            if (bassEvent) {
                // AUDIT FIX 1.6: Prevent sub-bass rumble (< 40Hz)
                const doubledFreq = bassEvent.freq / 2;
                if (doubledFreq >= 40) {
                    events.push({
                        ...bassEvent,
                        freq: doubledFreq, // Octave down
                        velocity: (bassEvent.velocity || 0.5) * 0.6, // Softer
                        delay: bassEvent.delay + 0.02 // Slight stagger
                    });
                }
            }
        }

        return events;
    }

    /**
     * V11 ENHANCED GHOST HAND: Intelligent Ghost Musician
     * Now uses GhostMusician for reactive counterpoint, walking bass, and fills.
     */
    getGhostEvent(session: ComposerSession, tuning: number = 440) {
        // 1. Get Context
        const currentSection = this.structureEngine.getCurrentSection();
        const constraints = session.getConstraints(currentSection);
        const scaleNotes = getScaleNotes(constraints);

        if (scaleNotes.length === 0) return [];

        const timeSinceLastTouch = Date.now() - this.lastTouchTime;

        // Calculate session intensity from journey
        const sessionDuration = this.sessionJourney.getSessionDuration();
        const sessionIntensity = Math.min(1, sessionDuration / 180000) * 0.5 + 0.3; // Ramps up over 3 minutes

        // Build ghost context
        const ghostContext: GhostContext = {
            lastMelodyNote: session.lastMelodyNote,
            lastMelodyDirection: this.ghostMusician.getMelodyDirection(),
            lastBassNote: session.currentBassNote,
            currentChord: session.currentChordContext,
            scaleNotes,
            currentSection,
            sessionIntensity,
            userYPosition: this.lastUserYPosition,
            timeSinceLastTouch,
            tuning
        };

        // Get intelligent ghost event
        const ghostEvent = this.ghostMusician.getGhostEvent(ghostContext);

        if (ghostEvent.notes.length === 0) {
            // Fallback to original simple pad behavior
            const bassNote = session.currentBassNote || scaleNotes[0];
            const padNote = bassNote - 12;
            const finalNote = Math.random() < 0.3 ? padNote + 7 : padNote;
            const freq = midiToFreq(finalNote + session.currentKeyOffset, tuning);

            return [{
                freq,
                delay: 0,
                duration: 2.0,
                velocity: 0.2,
                timbre: {
                    filter: 0.3,
                    release: 3.0,
                    reverb: 0.8
                }
            }];
        }

        // Return ghost musician's intelligent notes with timbre
        return ghostEvent.notes.map(note => ({
            freq: note.freq,
            delay: note.delay,
            duration: note.duration,
            velocity: note.velocity,
            timbre: {
                filter: 0.4, // Slightly dark
                release: 2.0,
                reverb: 0.7 // Ambient
            }
        }));
    }

    // =====================================================
    // V11: Additional Accessors
    // =====================================================

    /**
     * Get current arrangement density level.
     */
    getArrangementDensity(): 'sparse' | 'normal' | 'dense' {
        return this.arrangementIntelligence.getCurrentDensity();
    }

    /**
     * Get session journey phase.
     */
    getSessionPhase(): 'opening' | 'development' | 'climax' | 'resolution' {
        return this.sessionJourney.getHint(0).sessionPhase;
    }

    /**
     * Get ghost musician presence level.
     */
    getGhostPresence(): number {
        return this.ghostMusician.getPresenceLevel();
    }

    /**
     * Get song progress (0.0 to 1.0)
     */
    getSongProgress(): number {
        return this.structureEngine.getSongProgress();
    }
}
